import React, { useState, useEffect, useRef } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import AOS from "aos";
import { FlightSearch, HotelSearch, TrainSearch, BusSearch, HolidaySearch } from "../../Hero/Hero";
import "aos/dist/aos.css";
import "./Nav.css";
import Login from "../Login/Login";
import Signup from "../Signup/Signup";
import Logo from "../../../assets/logo.png";
import { useAuth } from "../../../AuthProvider/AuthContext";
import {
  BadgeCheck, Bus, BusFront, Car, Heart, Hotel, Landmark, LogOut, Palmtree, Plane,
  Train,
  TrainFront,
  Umbrella,
  User,
  IdCard,
  Headphones,
} from "lucide-react";
import { ENABLE_TRAINS } from "../../../config/features";

const NAV_LINKS = [
  { label: "Flights", icon: <Plane />, path: "/flights" },
  { label: "Hotels", icon: <Landmark />, path: "/hotels" },
  ...(ENABLE_TRAINS
  ? [{ label: "Trains", icon: <TrainFront />, path: "/trains" }]
  : []),
  { label: "Buses", icon: <BusFront />, path: "/buses" },
  { label: "Holidays", icon: <Umbrella />, path: "/holidays" },
  // { label: "Cabs", icon: <Car />, path: "/cabs" },
  { label: "Activities", icon: <Palmtree />, path: "/activities" },
  { label: "Visa", icon: <BadgeCheck />, path: "/visa" },
];
const OVERLAY_TAB_MAP = {
  "/flights": "flights",
  "/hotels": "hotels",
  "/trains": "trains",
  "/buses": "buses",
  "/holidays": "holidays",
};

const OVERLAY_TABS = [
  { id: "flights", label: "Flights", icon: <Plane size={16} /> },
  { id: "hotels", label: "Hotels", icon: <Landmark size={16} /> },
  { id: "trains", label: "Trains", icon: <TrainFront size={16} /> },
  { id: "buses", label: "Buses", icon: <BusFront size={16} /> },
  { id: "holidays", label: "Holidays", icon: <Umbrella size={16} /> },
];

const SUGGESTIONS = [
  { name: "Delhi → Mumbai", meta: "Flights · Economy", icon: <Plane size={18} /> },
  { name: "Hotels in Goa", meta: "Hotels · Jun 10–14", icon: <Hotel size={18} /> },
  { name: "Rajdhani Express", meta: "Train · Delhi → Kolkata", icon: <Train size={18} /> },
  { name: "Manali Holiday Package", meta: "Holidays · 5N/6D", icon: <Palmtree size={18} /> },
  { name: "Bangalore → Chennai Bus", meta: "Bus · Volvo AC Sleeper", icon: <Bus size={18} /> },
];

// ── Avatar initials helper ────────────────────────────────────
function getInitials(name = "", email = "") {
  const source = String(name || "").trim() || String(email || "").trim();

  if (!source) return "?";

  return source
    .split(/\s+/)
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function UserAvatar({ user, sizeClass = "" }) {
  const [imageFailed, setImageFailed] = useState(false);

  const avatarUrl = resolveAvatarUrl(user?.avatar);
  const initials = getInitials(user?.name, user?.email);

  useEffect(() => {
    setImageFailed(false);
  }, [user?.avatar]);

  return (
    <div className={`nb-avatar ${sizeClass}`}>
      {avatarUrl && !imageFailed ? (
        <img
          src={avatarUrl}
          alt={user?.name || "Profile"}
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span>{initials}</span>
      )}
    </div>
  );
}

// ── Profile Dropdown ──────────────────────────────────────────
function ProfileDropdown({ user, onLogout, onClose }) {
  const navigate = useNavigate();

  const go = (path) => { navigate(path); onClose(); };

  return (
    <div className="nb-profile-dropdown" onClick={(e) => e.stopPropagation()}>
      {/* User summary */}
      <div className="nb-profile-dropdown__header">
        <UserAvatar user={user} sizeClass="nb-avatar--md" />
        <div>
          <div className="nb-profile-dropdown__name">{user.name}</div>
          <div className="nb-profile-dropdown__email">{user.email}</div>
          {user.phone && (
            <div className="nb-profile-dropdown__phone">📞 {user.phone}</div>
          )}
        </div>
      </div>

      <div className="nb-profile-dropdown__divider" />

      {/* Menu items */}
      <button
        className="nb-profile-dropdown__item"
        onClick={() => go("/profile")}
      >
        <User size={18} />
        <span>My Profile</span>
      </button>

      {/* <button
        className="nb-profile-dropdown__item"
        onClick={() => go("/profile?tab=bookings")}
      >
        <Ticket size={18} />
        <span>My Bookings</span>
      </button> */}

      <button
        className="nb-profile-dropdown__item"
        onClick={() => go("/profile?tab=wishlist")}
      >
        <Heart size={18} />
        <span>Wishlist</span>
      </button>

      <button className="nb-profile-dropdown__item" onClick={() => go("/profile?tab=visa")}>
        <IdCard size={18} />
        <span>Visa Applications</span>
      </button>
      <div className="nb-profile-dropdown__divider" />

      <button
        className="nb-profile-dropdown__item nb-profile-dropdown__item--logout"
        onClick={onLogout}
      >
        <LogOut size={18} />
        <span>Log out</span>
      </button>
    </div>
  );
}
const API_ORIGIN = (process.env.REACT_APP_API_URL || "https://makebeetrip.com").replace(/\/api$/, "");

function resolveAvatarUrl(avatar) {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar)) return avatar;
  return `${API_ORIGIN}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
}
// ── Main Navbar ───────────────────────────────────────────────
export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const { isLoggedIn, user, logout } = useAuth();
  const [searchOverlayOpen, setSearchOverlayOpen] = useState(false);
  const [overlayTab, setOverlayTab] = useState("flights");

  const [menuOpen, setMenuOpen] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [searchVal, setSearchVal] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);  // ← new

  const searchRef = useRef(null);
  const profileRef = useRef(null);

  useEffect(() => { AOS.init({ duration: 600, once: true, easing: "ease-out-cubic" }); }, []);

  useEffect(() => {
  const onScroll = () => {
    setScrolled(window.scrollY > 10);
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll();

  return () => {
    window.removeEventListener("scroll", onScroll);
  };
}, []);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchFocused(false);
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    document.body.style.overflow = signupOpen || loginOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [signupOpen, loginOpen]);

useEffect(() => {
  setMenuOpen(false);
  setProfileOpen(false);
  window.scrollTo(0, 0);
}, [pathname]);

useEffect(() => {
  setSearchOverlayOpen(false);
}, [location.key]);

  useEffect(() => {
    if (!searchOverlayOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setSearchOverlayOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [searchOverlayOpen]);

  const filteredSuggestions = SUGGESTIONS.filter(
    (s) => searchVal === "" || s.name.toLowerCase().includes(searchVal.toLowerCase())
  );

  const handleLogout = () => {
    logout();
    setProfileOpen(false);
    navigate("/");
  };

  return (
    <>
      <nav
        className={[
  "navbar",
  scrolled ? "navbar--scrolled" : "",
  "navbar--links-visible",
].filter(Boolean).join(" ")}
      >
        {/* ── Top Row ── */}
        <div className="nb-top">
          <Link to="/" className="nb-logo">
            <div className="nb-logo__icon">
              <img src={Logo} alt="MakeBeeTrip Logo" />
            </div>
          </Link>

          {/* ── Category Links ── */}
          <div
  className={[
    "nb-links-wrap",
    menuOpen ? "menu-open" : "",
  ].filter(Boolean).join(" ")}
>
            <div className="nb-links">
              {NAV_LINKS.map((link) => {
                const overlayId = OVERLAY_TAB_MAP[link.path];
                return (
                  <NavLink
                    key={link.label}
                    to={link.path}
                    className={({ isActive }) => {
                      const isCitiesActivities = link.path === "/activities" && (
                        pathname.startsWith("/cities-activities") ||
                        pathname.startsWith("/international-activities")
                      );
                      const overlayActive = searchOverlayOpen && overlayTab === overlayId;
                      return `nb-link${isActive || isCitiesActivities || overlayActive ? " nb-link--active" : ""}`;
                    }}
                    onClick={(e) => {
                      if (overlayId) {
                        e.preventDefault();
                        if (searchOverlayOpen && overlayTab === overlayId) {
                          setSearchOverlayOpen(false);
                        } else {
                          setOverlayTab(overlayId);
                          setSearchOverlayOpen(true);
                        }
                        return;
                      }
                      setMenuOpen(false);
                    }}
                  >
                    <span className="nb-link__icon">{link.icon}</span>
                    <span className="nb-link__label">{link.label}</span>
                  </NavLink>
                );
              })}

              {/* Mobile auth inside drawer */}
              <div className="nb-links__mobile-auth">
                {isLoggedIn ? (
                  <>
                    <button
                      className="nb-btn nb-btn--ghost nb-btn--full"
                      onClick={() => { navigate("/profile"); setMenuOpen(false); }}
                    >My Profile</button>
                    <button
                      className="nb-btn nb-btn--primary nb-btn--full"
                      onClick={() => { handleLogout(); setMenuOpen(false); }}
                    >Log out</button>
                  </>
                ) : (
                  <>
                    <button
                      className="nb-btn nb-btn--ghost nb-btn--full"
                      onClick={() => { setLoginOpen(true); setMenuOpen(false); }}
                    >Log in</button>
                    <button
                      className="nb-btn nb-btn--primary nb-btn--full" style={{ color: "white" }}
                      onClick={() => { setSignupOpen(true); setMenuOpen(false); }}
                    >Sign up</button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="nb-actions">

            {/* ── SUPPORT ── */}
<button
  type="button"
  className="nb-support-btn"
  onClick={() => navigate("/contact")}
  aria-label="Customer support"
>
  <Headphones size={17} />
  <span>Support</span>
</button>

{/* ── AUTH SECTION ── */}
{isLoggedIn && user ? (
  <div className="nb-profile-wrap" ref={profileRef}>
    <button
      className="nb-avatar-btn"
      onClick={() => setProfileOpen((o) => !o)}
      aria-label="Open profile menu"
      aria-expanded={profileOpen}
    >
      <UserAvatar user={user} sizeClass="nb-avatar--sm" />

      <svg
        className={`nb-avatar-chevron${profileOpen ? " open" : ""}`}
        width="17"
        height="17"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </button>

    {profileOpen && (
      <ProfileDropdown
        user={user}
        onLogout={handleLogout}
        onClose={() => setProfileOpen(false)}
      />
    )}
  </div>
) : (
  <div
    className="nb-auth"
    data-aos="fade-down"
    data-aos-delay="120"
  >
    <button
      className="nb-btn nb-btn--ghost"
      onClick={() => setLoginOpen(true)}
    >
      Log in
    </button>

    <button
      className="nb-btn nb-btn--primary"
      onClick={() => setSignupOpen(true)}
    >
      Sign up
    </button>
  </div>
)}

            <button
              className={`nb-hamburger${menuOpen ? " nb-hamburger--open" : ""}`}
              onClick={() => setMenuOpen(o => !o)}
              aria-label="Toggle menu"
            >
              <span /><span /><span />
            </button>
          </div>
        </div>

        {/* ── Suggestions dropdown ── */}
        {searchFocused && filteredSuggestions.length > 0 && (
          <div className="nb-suggestions" ref={searchRef}>
            <p className="nb-suggestions__label">Popular searches</p>
            {filteredSuggestions.map((s, i) => (
              <div
                key={i}
                className="nb-suggestions__item"
                onMouseDown={() => { setSearchVal(s.name); setSearchFocused(false); }}
              >
                <span className="nb-suggestions__emoji">{s.icon}</span>
                <span>
                  <span className="nb-suggestions__name">{s.name}</span>
                  <span className="nb-suggestions__meta">{s.meta}</span>
                </span>
              </div>
            ))}
          </div>
        )}
        
      </nav>
     {searchOverlayOpen && (
  <div
    className="nb-search-overlay-backdrop"
    onClick={() => setSearchOverlayOpen(false)}
  >
    <div className="nb-search-overlay-panel" onClick={(e) => e.stopPropagation()}>
      <button
        className="nb-search-overlay-close"
        onClick={() => setSearchOverlayOpen(false)}
        aria-label="Close search"
      >
        ✕
      </button>
      <div className="nb-search-overlay-body">
        {overlayTab === "flights" && <FlightSearch inNavbar />}
        {overlayTab === "hotels" && <HotelSearch inNavbar />}
        {overlayTab === "trains" && <TrainSearch inNavbar />}
        {overlayTab === "buses" && <BusSearch inNavbar />}
        {overlayTab === "holidays" && <HolidaySearch inNavbar />}
      </div>
    </div>
  </div>
)}

      {loginOpen && (
        <Login
          onClose={() => setLoginOpen(false)}
          onSwitchToSignup={() => { setSignupOpen(true); setLoginOpen(false); }}
        />
      )}
      {signupOpen && (
        <Signup
          onClose={() => setSignupOpen(false)}
          onSwitchToLogin={() => { setLoginOpen(true); setSignupOpen(false); }}
        />
      )}
    </>
  );
}
